import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getCookie, setCookie, extractEmailFromUserIdCookie } from '@/src/utils/browser'
import { RootState } from '../rootReducer'
import { isFlyin } from '@/src/utils/generalUtils/isFlyin'

export interface UserProfile {
    firstName?: string
    lastName?: string
    fullName?: string
    picture?: string
    googleId?: string
    mobile?: string
    countryCode?: string
    displayName?: string
}

export interface ClevertapProfilePayload {
    Identity: string | number
    Email: string
    Phone: string
    Gender?: string
    'MSG-whatsapp': boolean
    'MSG-sms': boolean
    'MSG-email': boolean
    'MSG-push': boolean
    Name?: string
    DOB?: string
    user_language?: string
    page_country?: string
}

export interface AuthState extends UserProfile {
    signedIn: boolean
    email?: string
    userId?: string | number
    ctAuth?: string
    clevertapProfile?: ClevertapProfilePayload | null
}

/**
 * Safely parses user data from cookies
 */
const parseUserData = (userData: string | null): Partial<UserProfile> => {
    if (!userData) return {}

    try {
        return JSON.parse(decodeURIComponent(userData))
    } catch (error) {
        console.error('Error parsing user data from cookie:', error)
        return {}
    }
}

/**
 * Extracts user ID from the userid cookie
 */
const extractUserId = (userIdCookie: string | null): string | undefined => {
    if (!userIdCookie) return undefined

    const idMatch = userIdCookie.match(/\|(\d+)\|/)
    return idMatch?.[1]
}

/**
 * Gets the initial auth state from cookies (client-side only)
 */
const getAuthStateFromCookies = (): AuthState => {
    const usermisc = getCookie('usermisc')
    const userid = getCookie('userid')
    const userData = getCookie('userData')

    // If not signed in, return default state
    if (!usermisc || !usermisc.includes('SIGNED_IN')) {
        return { signedIn: false }
    }

    // Parse user data from cookies
    const userInfo = parseUserData(userData || null)
    const email = userid ? extractEmailFromUserIdCookie(userid) : undefined
    const userId = userid ? extractUserId(userid) : (userInfo as { userId?: string | number })?.userId

    return {
        signedIn: true,
        email,
        userId,
        ...userInfo
    }
}

// Initialize from cookies on client-side, use default on server to prevent hydration mismatch
const initialState: AuthState = typeof window !== 'undefined' ? getAuthStateFromCookies() : { signedIn: false }

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        /**
         * Updates the auth state with new user data and persists displayName to cookie
         */
        setAuthUser: (state, action: PayloadAction<Partial<AuthState>>) => {
            const newState = { ...state, ...action.payload }

            // Save displayName to userData cookie for persistence across page reloads
            if (typeof window !== 'undefined' && newState.displayName) {
                try {
                    const domain = isFlyin() ? '.flyin.com' : ('.' + window.location.hostname)
                    setCookie(
                        'userData',
                        encodeURIComponent(JSON.stringify({ displayName: newState.displayName })),
                        365,
                        domain
                    )
                } catch (error) {
                    console.error('Error saving user data to cookie:', error)
                }
            }

            return newState
        },

        setClevertapProfile: (state, action: PayloadAction<ClevertapProfilePayload | null>) => {
            state.clevertapProfile = action.payload
        },

        /**
         * Clears the auth state (on sign out) and removes userData cookie
         */
        clearAuthUser: () => {
            // Clear userData cookie on sign out
            if (typeof window !== 'undefined') {
                try {
                    const domain = isFlyin() ? '.flyin.com' : ('.' + window.location.hostname)
                    setCookie('userData', '', -1, domain) // Set expired cookie to delete it
                } catch (error) {
                    console.error('Error clearing userData cookie:', error)
                }
            }

            return {
                signedIn: false,
                email: undefined,
                userId: undefined,
                firstName: undefined,
                lastName: undefined,
                fullName: undefined,
                picture: undefined,
                googleId: undefined,
                mobile: undefined,
                countryCode: undefined,
                displayName: undefined,
                clevertapProfile: null
            }
        },

        /**
         * Syncs auth fields from cookies while preserving in-memory clevertapProfile.
         */
        refreshAuthUser: state => {
            const fromCookies = getAuthStateFromCookies()

            if (!fromCookies.signedIn) {
                return { ...fromCookies, clevertapProfile: null }
            }

            return {
                ...fromCookies,
                clevertapProfile: state.clevertapProfile ?? null
            }
        }
    }
})

// Action creators
export const { setAuthUser, setClevertapProfile, clearAuthUser, refreshAuthUser } = authSlice.actions

// Selectors
export const selectAuth = (state: RootState) => state.auth
export const selectClevertapProfile = (state: RootState) => state.auth.clevertapProfile
export const selectIsAuthenticated = (state: RootState) => state.auth.signedIn
export const selectCurrentUser = (state: RootState) => ({
    email: state.auth.email,
    userId: state.auth.userId,
    fullName: state.auth.fullName,
    firstName: state.auth.firstName,
    lastName: state.auth.lastName,
    picture: state.auth.picture
})

export default authSlice.reducer
